import { Injectable, Injector, NgZone, inject } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import {
  ActiveToast,
  IndividualConfig,
  Overlay,
  TOAST_CONFIG,
  ToastrService,
} from 'ngx-toastr';

/**
 * Toast messages are passed as plain English strings all over the app and inside
 * @ace/ace, so there is no single template to hang a translate pipe on. Overriding
 * the service keeps every existing call site untouched while still running the
 * message and title through the dictionary.
 */
@Injectable()
export class TranslatingToastrService extends ToastrService {
  private readonly translate = inject(TranslateService);

  constructor() {
    super(
      inject(TOAST_CONFIG),
      inject(Overlay),
      inject(Injector),
      inject(DomSanitizer),
      inject(NgZone)
    );
  }

  override show<T = any>(
    message?: string,
    title?: string,
    override?: Partial<IndividualConfig<T>>,
    type?: string
  ): ActiveToast<any> {
    return super.show(this.translateText(message), this.translateText(title), override, type);
  }

  override success<T = any>(
    message?: string,
    title?: string,
    override?: Partial<IndividualConfig<T>>
  ): ActiveToast<any> {
    return super.success(this.translateText(message), this.translateText(title), override);
  }

  override error<T = any>(
    message?: string,
    title?: string,
    override?: Partial<IndividualConfig<T>>
  ): ActiveToast<any> {
    return super.error(this.translateText(message), this.translateText(title), override);
  }

  override info<T = any>(
    message?: string,
    title?: string,
    override?: Partial<IndividualConfig<T>>
  ): ActiveToast<any> {
    return super.info(this.translateText(message), this.translateText(title), override);
  }

  override warning<T = any>(
    message?: string,
    title?: string,
    override?: Partial<IndividualConfig<T>>
  ): ActiveToast<any> {
    return super.warning(this.translateText(message), this.translateText(title), override);
  }

  /** Server-supplied text is not a dictionary key, so it comes back unchanged. */
  private translateText(value?: string): string | undefined {
    if (!value) {
      return value;
    }
    const translated = this.translate.instant(value);
    return typeof translated === 'string' ? translated : value;
  }
}
